import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { TextComponent } from "../../../../core/components/TextComponent";
import { RootState } from "../../../../core/store/store";
import { Switch } from "../../../../core/components/Switch";
import { Column } from "../../../../core/components/Column";
import { useDebouncedConfig } from "../hook/useDebounceConfig";
import { apiServices } from "../../../../core/utilities/ApiServices";
import { ModalSimple } from "../../../../core/components/Modal";
import { SignedInLikeEmailPreference } from "./SignedInLikeEmailPreference";

interface NotificationsSettingsState {
	show_last_logged: boolean | undefined;
	matches: boolean | undefined;
	chat: boolean | undefined;
	likes: boolean | undefined;
	like_distance: boolean | undefined;
}

export const SettingsNotification = () => {
	const navigate = useNavigate();
	const { getProfileConfig } = useSelector((state: RootState) => state.onboarding);
	const [showSubscribePrompt, setShowSubscribePrompt] = useState(false);
	const [newNearbyMembers, setNewNearbyMembers] = useState({
		enabled: false,
		isPaid: false,
		loading: true,
		saving: false,
	});
	const defaultOn = (value: boolean | undefined) => value ?? true;
	const initialValues: NotificationsSettingsState = {
		show_last_logged: defaultOn(getProfileConfig?.search_filters?.show_last_logged),
		matches: defaultOn(getProfileConfig?.search_filters?.matches),
		chat: defaultOn(getProfileConfig?.search_filters?.chat),
		likes: defaultOn(getProfileConfig?.search_filters?.likes),
		like_distance: getProfileConfig?.search_filters?.like_distance,
	};
	const [notificationSettings, setNotificationSettings] = useDebouncedConfig<NotificationsSettingsState>({
		initialValues,
		buildUpdatedFilters: (settings) => ({
			search_filters: {
				show_last_logged: settings.show_last_logged,
				matches: settings.matches,
				chat: settings.chat,
				likes: settings.likes,
				like_distance: settings.like_distance,
			},
		}),
	});
	const handleSwitchChange = (key: string, value: boolean) => {
		setNotificationSettings((prevState) => ({
			...prevState,
			[key]: value,
		}));
	};
	useEffect(() => {
		let mounted = true;
		apiServices
			.get("/new-nearby-members-alerts/settings")
			.then((response) => {
				if (!mounted) return;
				setNewNearbyMembers({
					enabled: response.data?.notify_new_nearby_members === true,
					isPaid: response.data?.is_paid === true,
					loading: false,
					saving: false,
				});
			})
			.catch(() => {
				if (!mounted) return;
				setNewNearbyMembers((current) => ({
					...current,
					loading: false,
					saving: false,
				}));
			});

		return () => {
			mounted = false;
		};
	}, []);

	const handleNewNearbyMembersChange = async () => {
		if (!newNearbyMembers.isPaid || newNearbyMembers.loading || newNearbyMembers.saving) return;
		const nextEnabled = !newNearbyMembers.enabled;
		setNewNearbyMembers((current) => ({
			...current,
			enabled: nextEnabled,
			saving: true,
		}));

		try {
			const response = await apiServices.patch("/new-nearby-members-alerts/settings", {
				notify_new_nearby_members: nextEnabled,
			});
			setNewNearbyMembers({
				enabled: response.data?.notify_new_nearby_members === true,
				isPaid: response.data?.is_paid === true,
				loading: false,
				saving: false,
			});
		} catch {
			setNewNearbyMembers((current) => ({
				...current,
				enabled: !nextEnabled,
				saving: false,
			}));
		}
	};
	const handleLockedNewNearbyMembersClick = () => {
		if (newNearbyMembers.isPaid || newNearbyMembers.loading) return;
		setShowSubscribePrompt(true);
	};

	return (
		<>
			<TextComponent
				variant="h2"
				className="font-semibold mt-7 mb-5"
			>
				Notifications
			</TextComponent>
			<Column className="grid grid-cols-2 md:grid-cols-3 gap-4">
				<Switch
					label="Show last logged"
					initialChecked={notificationSettings.show_last_logged ?? true}
					onChange={(newValue: boolean) => handleSwitchChange("show_last_logged", newValue)}
				/>
				<Switch
					label="Chat"
					initialChecked={notificationSettings.chat ?? true}
					onChange={(newValue: boolean) => handleSwitchChange("chat", newValue)}
				/>
				<Switch
					label="Matches"
					initialChecked={notificationSettings.matches ?? true}
					onChange={(newValue: boolean) => handleSwitchChange("matches", newValue)}
				/>
				<Switch
					label="Likes"
					initialChecked={notificationSettings.likes ?? true}
					onChange={(newValue: boolean) => handleSwitchChange("likes", newValue)}
				/>
				<Column className="gap-2" onClick={handleLockedNewNearbyMembersClick}>
					<TextComponent
						variant="h6"
						className={!newNearbyMembers.isPaid ? "text-gray-400" : ""}
					>
						New nearby members
					</TextComponent>
					<label
						className={`relative w-fit items-center ${
							newNearbyMembers.isPaid && !newNearbyMembers.loading && !newNearbyMembers.saving
								? "cursor-pointer"
								: "cursor-not-allowed opacity-60"
						}`}
						onClick={handleLockedNewNearbyMembersClick}
					>
						<input
							type="checkbox"
							className="sr-only peer"
							checked={newNearbyMembers.enabled && newNearbyMembers.isPaid}
							disabled={!newNearbyMembers.isPaid || newNearbyMembers.loading || newNearbyMembers.saving}
							onChange={handleNewNearbyMembersChange}
						/>
						<div
							onClick={handleLockedNewNearbyMembersClick}
							className="group peer bg-white rounded-full duration-300 w-16 h-8 ring-2 ring-primary/60 after:duration-300 after:bg-primary/60 peer-checked:after:bg-primary peer-checked:ring-primary after:rounded-full after:absolute after:h-6 after:w-6 after:top-1 after:left-1 after:flex after:justify-center after:items-center peer-checked:after:translate-x-8"
						/>
					</label>
				</Column>
			</Column>
			<SignedInLikeEmailPreference />
			<ModalSimple
				title="Paid subscription feature"
				actionExecuted={showSubscribePrompt}
				onClose={() => setShowSubscribePrompt(false)}
				endButtons={
					<div className="grid grid-cols-2 gap-3">
						<button
							type="button"
							className="rounded-md border border-primary px-4 py-3 text-primary"
							onClick={() => setShowSubscribePrompt(false)}
						>
							Not now
						</button>
						<button
							type="button"
							className="rounded-md bg-primary px-4 py-3 text-white"
							onClick={() => {
								setShowSubscribePrompt(false);
								navigate("/subscription");
							}}
						>
							Subscribe now
						</button>
					</div>
				}
			>
				Subscribe to get notified when new nearby members appear within your distance settings.
			</ModalSimple>
		</>
	);
};
